import {
  createFileRoute,
  useRouter,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import { useCallback } from "react"
import ScreenWrapper from "@/components/ScreenWrapper"
import { getFilteredGroups } from "@/api/services/groups"
import type { ColumnDef } from "@tanstack/react-table"
import {
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  Group,
  groupQueryKeys,
  TABLE_PAGE_SIZE,
  DataTable,
  selectColumn,
  createSortableColumn,
  createCustomColumn,
  formatLearningPathName,
  AbbreviateTableColumn,
  getFullName,
} from "toptaal-shared"
import { checkOrganizationPermissions } from "@/utils/checkOrganizationPermissions"

export const Route = createFileRoute("/beheer/groepen/")({
  component: RouteComponent,
  validateSearch: createTableSearchSchema(),
  beforeLoad: () => checkOrganizationPermissions("read", "groups"),
})

function RouteComponent() {
  const router = useRouter()
  const searchParams = useSearch({ from: "/beheer/groepen/" })
  const navigate = useNavigate({
    from: "/beheer/groepen/",
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
  } = usePaginatedTableWithUrlState({
    queryKey: groupQueryKeys.all,
    queryFn: getFilteredGroups,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: "/beheer/groepen",
  })

  const handleNavigateDetail = useCallback(
    (groupId: Group["id"]) => {
      router.navigate({
        to: "/beheer/groepen/$groupId/students",
        params: { groupId: groupId.toString() },
        search: { from: router.state.location.href },
      })
    },
    [router],
  )

  const columns: ColumnDef<Group>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createCustomColumn({
      id: "project_names",
      name: "Contract",
      cell: ({ row }) => (
        <AbbreviateTableColumn
          entities={row.original.projects.map((p) => ({ name: p.name }))}
          cutOffCount={1}
        />
      ),
      accessorKey: "projects",
    }),
    createCustomColumn({
      id: "learning_path_name",
      name: "Leerlijn",
      cell: ({ row }) => (
        <AbbreviateTableColumn
          entities={row.original.learningPaths
            .map((lp) => formatLearningPathName({ learningPath: lp }))
            .filter(Boolean)
            .map((name) => ({ name: name! }))}
          cutOffCount={1}
        />
      ),
      accessorKey: "learningPaths",
    }),
    createCustomColumn({
      id: "students",
      name: "Cursisten",
      cell: ({ row }) => `${row.original.studentsCount ?? 0} cursisten`,
      accessorKey: "studentsCount",
    }),
    createCustomColumn({
      id: "capacity",
      name: "Maximale groepsgrootte",
      cell: ({ row }) => `${row.original.capacity ?? ""}`,
    }),
    createCustomColumn({
      id: "planners",
      name: "Planners",
      cell: ({ row }) => (
        <AbbreviateTableColumn
          entities={row.original.planners.map((planner) => {
            return {
              name: getFullName({
                firstName: planner.firstName,
                lastName: planner.lastName,
              }),
            }
          })}
        />
      ),
      accessorKey: "planners",
    }),
  ]

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Groepen"
        data={data?.data.lessonGroups || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        onRowClick={handleNavigateDetail}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        columns={columns}
        headerRightActions={
          <>
            {/* TODO: figure out way to move to shared */}
            {/* <TableHeaderActions
              exportAction={{
                onClick: exportGroups,
              }}
            /> */}
          </>
        }
      />
    </ScreenWrapper>
  )
}
